/**
 * Transforms golfcourseapi.com response to internal course format.
 * Shared by the scraper script and the app's online search fallback.
 */

/**
 * Map an API course response to internal format.
 * @param {object} apiCourse - Raw response from golfcourseapi.com /v1/courses/:id
 * @returns {object|null} Internal course object, or null if data is insufficient
 */
export function mapApiCourse(apiCourse) {
  if (!apiCourse) return null

  const id = String(apiCourse.id || apiCourse.course_id || '')
  if (!id) return null

  const courseName = apiCourse.course_name || apiCourse.name || ''
  if (!courseName) return null

  // Extract tees — male tees are primary, female tees provide women's slope/rating
  const maleTees = apiCourse.tees?.male || apiCourse.teeboxes?.filter(t => t.gender === 'male') || []
  const femaleTees = apiCourse.tees?.female || apiCourse.teeboxes?.filter(t => t.gender === 'female') || []
  const femaleTeeByName = new Map(femaleTees.map(t => [(t.tee_name || t.name || '').toUpperCase(), t]))

  // Get par and handicap index from the first tee set that has hole data
  let par = null
  let handicapIndex = null

  for (const tee of maleTees) {
    const holes = tee.holes || []
    if (holes.length >= 18) {
      const sortedHoles = [...holes].sort((a, b) => (a.hole_number || a.number || 0) - (b.hole_number || b.number || 0))
      const pars = sortedHoles.map(h => h.par).filter(p => p != null)
      const hcps = sortedHoles.map(h => h.handicap || h.hdcp || h.handicap_index).filter(h => h != null)

      if (pars.length >= 18 && !par) {
        par = pars.slice(0, 18)
      }
      if (hcps.length >= 18 && !handicapIndex) {
        handicapIndex = hcps.slice(0, 18)
      }
      if (par && handicapIndex) break
    }
  }

  // par is required
  if (!par || par.length < 18) return null
  // handicapIndex may be missing — we'll accept the course without it
  if (!handicapIndex || handicapIndex.length < 18) {
    handicapIndex = Array.from({ length: 18 }, (_, i) => i + 1) // fallback: sequential
  }

  // Map tees
  const tees = maleTees
    .map(tee => {
      const name = tee.tee_name || tee.name || tee.tee || ''
      if (!name) return null

      const holes = tee.holes || []
      const sortedHoles = [...holes].sort((a, b) => (a.hole_number || a.number || 0) - (b.hole_number || b.number || 0))
      const yardages = sortedHoles.map(h => h.yardage || h.yards || h.length || 0)

      // Extract per-tee handicap rankings (may differ from course-level)
      const teeHcps = sortedHoles.map(h => h.handicap || h.hdcp || h.handicap_index).filter(h => h != null)
      const teeHandicapIndex = teeHcps.length >= 18 ? teeHcps.slice(0, 18) : undefined

      // Match female tee by name for women's ratings and handicap rankings
      const femaleTee = femaleTeeByName.get(name.toUpperCase())
      const womenRating = femaleTee?.course_rating || femaleTee?.rating || null
      const womenSlope = femaleTee?.slope_rating || femaleTee?.slope || null

      // Extract women's hole handicap rankings from the matched female tee
      let womenHandicapIndex = undefined
      if (femaleTee?.holes?.length >= 18) {
        const sortedFemaleHoles = [...femaleTee.holes].sort((a, b) => (a.hole_number || a.number || 0) - (b.hole_number || b.number || 0))
        const femaleHcps = sortedFemaleHoles.map(h => h.handicap || h.hdcp || h.handicap_index).filter(h => h != null)
        if (femaleHcps.length >= 18) womenHandicapIndex = femaleHcps.slice(0, 18)
      }

      return {
        name,
        rating: tee.course_rating || tee.rating || null,
        slope: tee.slope_rating || tee.slope || null,
        ...(womenRating != null && { womenRating }),
        ...(womenSlope != null && { womenSlope }),
        totalYardage: tee.total_yards || tee.total_yardage || yardages.reduce((s, y) => s + y, 0),
        yardages: yardages.length >= 18 ? yardages.slice(0, 18) : [],
        ...(teeHandicapIndex && { handicapIndex: teeHandicapIndex }),
        ...(womenHandicapIndex && { womenHandicapIndex }),
      }
    })
    .filter(t => t != null && t.name)

  if (tees.length === 0) return null

  // Location
  const location = apiCourse.location || {}
  const city = location.city || apiCourse.city || null
  const state = location.state || apiCourse.state || null
  const country = location.country || apiCourse.country || null

  // Coordinates
  const latitude = location.latitude ?? location.lat ?? null
  const longitude = location.longitude ?? location.lng ?? location.lon ?? null

  // Generate short name
  const shortName = generateShortName(courseName, apiCourse.club_name)

  return {
    id,
    source: 'api',
    club_name: apiCourse.club_name || null,
    course_name: courseName,
    short_name: shortName,
    // camelCase aliases for app scoring/display code
    name: courseName,
    shortName,
    handicapIndex,
    // snake_case for Supabase row mapping
    handicap_index: handicapIndex,
    city,
    state,
    country,
    holes: 18,
    par,
    tees,
    latitude,
    longitude,
  }
}

/**
 * Generate a short display name for a course.
 */
function generateShortName(courseName, clubName) {
  // If course name is already short enough, use it
  if (courseName.length <= 20) return courseName

  // Try to extract a meaningful short name
  // Remove common suffixes
  let short = courseName
    .replace(/\s*Golf\s*(Course|Club|Links|Resort)?\s*$/i, '')
    .replace(/\s*Country\s*Club\s*$/i, '')
    .replace(/\s*at\s+.*$/i, '')
    .trim()

  if (short.length <= 20) return short

  // If still too long, take the first meaningful words
  return short.split(/\s+/).slice(0, 3).join(' ')
}

/**
 * Map a search result item (lighter than full course detail).
 * Used for displaying search results before fetching full details.
 */
export function mapApiSearchResult(item) {
  if (!item) return null

  const loc = item.location || {}
  return {
    id: String(item.id || item.course_id || ''),
    course_name: item.course_name || item.name || '',
    club_name: item.club_name || null,
    city: loc.city || item.city || null,
    state: loc.state || item.state || null,
    country: loc.country || item.country || null,
    latitude: loc.latitude ?? loc.lat ?? null,
    longitude: loc.longitude ?? loc.lng ?? loc.lon ?? null,
  }
}

/**
 * Merge manually-corrected per-tee fields (e.g. handicapIndex) from existing
 * DB tees into freshly-mapped API tees, so an API re-fetch doesn't overwrite
 * manual corrections stored in Supabase.
 *
 * @param {Array} newTees - tees from mapApiCourse()
 * @param {Array} existingTees - tees currently stored in Supabase (parsed JSON)
 * @returns {Array} newTees with preserved manual overrides
 */
export function mergeTeeOverrides(newTees, existingTees) {
  if (!existingTees?.length) return newTees

  const existingByName = new Map(existingTees.map(t => [t.name, t]))
  return newTees.map(t => {
    const existing = existingByName.get(t.name)
    if (!existing) return t
    const merged = { ...t }
    // Preserve handicapIndex if the new tee doesn't provide one but the DB has one
    if (!merged.handicapIndex && existing.handicapIndex) {
      merged.handicapIndex = existing.handicapIndex
    }
    // Preserve women's ratings if the new tee doesn't provide them but the DB has them
    if (merged.womenRating == null && existing.womenRating != null) {
      merged.womenRating = existing.womenRating
    }
    if (merged.womenSlope == null && existing.womenSlope != null) {
      merged.womenSlope = existing.womenSlope
    }
    if (!merged.womenHandicapIndex && existing.womenHandicapIndex) {
      merged.womenHandicapIndex = existing.womenHandicapIndex
    }
    return merged
  })
}

/**
 * Convert internal course format to Supabase row format.
 * Used by the scraper and the online search cache.
 */
export function courseToSupabaseRow(course) {
  return {
    id: course.id,
    source: course.source || 'api',
    club_name: course.club_name,
    course_name: course.course_name,
    short_name: course.short_name,
    city: course.city,
    state: course.state,
    country: course.country,
    holes: course.holes || 18,
    par: course.par,
    handicap_index: course.handicap_index,
    tees: JSON.stringify(course.tees),
    latitude: course.latitude ?? null,
    longitude: course.longitude ?? null,
  }
}
